import _defineProperty from "@babel/runtime/helpers/defineProperty";
import { getFieldPropertiesByTarget } from '@wix/form-viewer/controller';
import { createOrderAndCharge } from '@wix/ambassador-ecom-v1-checkout/http';
import { cashierContactToUpdateAddressRequest, cashierContactToUpdateContactRequest } from '../../utils/addresses.utils';
import { ambassadorWithHeaders, getHeaders } from '../../utils/ambassador.utils';
import { FORMS_TEMPLATE_IDS, FQDN, FormFieldTarget, ECOM_PLATFORM_APP_DEF_ID, CART_V2_PREFIX } from '../../utils/consts';
import { createClient } from '@wix/sdk/client';
import * as ecomCheckout from '@wix/auto_sdk_ecom_checkout';
import { ExpressModel } from '../../models/Express.model';
import { createDeliveryOptionsServiceGetter } from '../deliveryOptionsGetter';
import { hasShippableItems } from '../../utils/hasShippableItems';
import { getShippingMethods } from '../../utils/getShippingMethods';
import { SPECS } from '../SPECS';
import { LineItemModel } from '../../models/LineItem.model';
export class CheckoutService {
  constructor(_ref) {
    let {
      httpClient,
      errorHandler,
      controllerConfig: _controllerConfig,
      getCurrency,
      locale
    } = _ref;
    _defineProperty(this, "requestWithHeaders", void 0);
    _defineProperty(this, "checkoutApi", void 0);
    _defineProperty(this, "httpClient", void 0);
    _defineProperty(this, "errorHandler", void 0);
    _defineProperty(this, "controllerConfig", void 0);
    _defineProperty(this, "deliveryOptionsServiceGetter", void 0);
    _defineProperty(this, "useCartDirectlyFromV2", checkoutId => {
      return this.controllerConfig.essentials.experiments.enabled(SPECS.CallCartV2FromExpressCheckout) && checkoutId.startsWith(CART_V2_PREFIX);
    });
    _defineProperty(this, "fetchDeliveryOptionsForV1", async sdkCheckout => {
      var _sdkCheckout$shipping;
      const service = await this.deliveryOptionsServiceGetter();
      return service.getDeliveryOptions(sdkCheckout, (_sdkCheckout$shipping = sdkCheckout.shippingInfo) == null || (_sdkCheckout$shipping = _sdkCheckout$shipping.selectedCarrierServiceOption) == null ? void 0 : _sdkCheckout$shipping.code);
    });
    _defineProperty(this, "fetchDeliveryOptions", async cart => {
      var _cart$deliveryInfo;
      const service = await this.deliveryOptionsServiceGetter();
      return service.getDeliveryOptionsFromCartV2(cart, (_cart$deliveryInfo = cart.deliveryInfo) == null || (_cart$deliveryInfo = _cart$deliveryInfo.method) == null ? void 0 : _cart$deliveryInfo.code);
    });
    _defineProperty(this, "getApiBaseUrl", controllerConfig => {
      const isSSR = controllerConfig.wixCodeApi.window.rendering.env === 'backend';
      if (isSSR) {
        return '';
      }
      return location.host;
    });
    this.httpClient = httpClient;
    this.errorHandler = errorHandler;
    this.controllerConfig = _controllerConfig;
    this.requestWithHeaders = errorCodesMap => ambassadorWithHeaders(httpClient, _controllerConfig, getCurrency(), errorHandler, errorCodesMap);
    let ecomInstance = _controllerConfig.appParams.instance;
    _controllerConfig.wixCodeApi.site.onInstanceChanged(_ref2 => {
      let {
        instance
      } = _ref2;
      ecomInstance = instance;
    }, ECOM_PLATFORM_APP_DEF_ID);
    const wixClient = createClient({
      auth: {
        getAuthHeaders: () => ({
          headers: {
            Authorization: ecomInstance,
            ...getHeaders(_controllerConfig, getCurrency())
          }
        })
      },
      headers: {},
      modules: {
        checkout: {
          getCheckout: ecomCheckout.getCheckout,
          updateCheckout: ecomCheckout.updateCheckout
        }
      },
      host: {
        apiBaseUrl: this.getApiBaseUrl(_controllerConfig)
      }
    });
    this.checkoutApi = wixClient.checkout;
    this.deliveryOptionsServiceGetter = createDeliveryOptionsServiceGetter(locale, httpClient, _controllerConfig, errorHandler);
  }
  async getCheckoutByCheckoutId(checkoutId) {
    var _this$checkoutApi;
    if (this.useCartDirectlyFromV2(checkoutId)) {
      const publicApi = await this.controllerConfig.wixCodeApi.site.getPublicAPI(ECOM_PLATFORM_APP_DEF_ID);
      const {
        cart,
        summary
      } = await publicApi.__internal.calculateCart(checkoutId);
      return ExpressModel.fromV2(cart, summary, this.getModelContext(cart));
    }
    const checkout = await ((_this$checkoutApi = this.checkoutApi) == null ? void 0 : _this$checkoutApi.getCheckout(checkoutId));
    return ExpressModel.fromV1(checkout, this.getModelContextFromV1(checkout));
  }
  async doesFormHaveRequiredDataExtendedFields() {
    const fieldProps = await getFieldPropertiesByTarget({
      options: {
        fqdn: FQDN
      },
      httpClient: this.httpClient,
      formId: FORMS_TEMPLATE_IDS.CUSTOMER_DETAILS_WO_ADDITIONAL_INFO,
      errorHandler: this.errorHandler
    });
    const fieldValues = fieldProps ? Object.values(fieldProps) : [];
    return fieldValues.filter(fieldValue => fieldValue == null ? void 0 : fieldValue.required).length > 0;
  }
  async doesFormHaveRequiredAdditionalInfoField() {
    var _fieldProps$FormField;
    const fieldProps = await getFieldPropertiesByTarget({
      httpClient: this.httpClient,
      formId: FORMS_TEMPLATE_IDS.CUSTOMER_DETAILS_WO_ADDITIONAL_INFO,
      errorHandler: this.errorHandler
    });
    return Boolean(fieldProps && (/* istanbul ignore next */(_fieldProps$FormField = fieldProps[FormFieldTarget.additional_info_1]) == null ? void 0 : _fieldProps$FormField.required));
  }
  async placeOrder(_ref3) {
    let {
      id,
      paymentToken,
      redirectUrls,
      priceVerificationToken
    } = _ref3;
    const publicApi = await this.controllerConfig.wixCodeApi.site.getPublicAPI(ECOM_PLATFORM_APP_DEF_ID);
    const data = await publicApi.__internal.placeOrder(id, {
      paymentToken,
      redirectUrls,
      priceVerificationToken
    });
    const orderId = data.orderId;
    const paymentResponseToken = data.paymentResponseToken;
    return {
      orderId,
      paymentResponseToken
    };
  }
  async placeOrderV1(_ref4) {
    let {
      id,
      paymentToken,
      redirectUrls
    } = _ref4;
    let orderId;
    let paymentResponseToken;
    try {
      const request = {
        id,
        paymentToken,
        urlParams: redirectUrls
      };
      const {
        data
      } = await this.requestWithHeaders({})(createOrderAndCharge(request));
      orderId = data.orderId;
      paymentResponseToken = data.paymentResponseToken;
    } catch {
      orderId = undefined;
      paymentResponseToken = undefined;
    }
    return {
      orderId,
      paymentResponseToken
    };
  }
  async updateDeliveryAddress(checkoutId, address) {
    var _this$checkoutApi2;
    if (this.useCartDirectlyFromV2(checkoutId)) {
      const publicApi = await this.controllerConfig.wixCodeApi.site.getPublicAPI(ECOM_PLATFORM_APP_DEF_ID);
      const cart = await publicApi.__internal.updateCart(checkoutId, {
        deliveryInfo: {
          address: cashierContactToUpdateAddressRequest(address)
        }
      });
      const [{
        summary
      }, deliveryOptions] = await Promise.all([publicApi.__internal.calculateCart(checkoutId), this.fetchDeliveryOptions(cart)]);
      const expressModel = ExpressModel.fromV2(cart, summary, this.getModelContext(cart));
      return {
        expressModel,
        shippingMethods: getShippingMethods(deliveryOptions, expressModel.deliveryMethodCode)
      };
    }
    const checkout = await ((_this$checkoutApi2 = this.checkoutApi) == null ? void 0 : _this$checkoutApi2.updateCheckout(checkoutId, {
      shippingInfo: {
        shippingDestination: {
          address: {
            country: (address == null ? void 0 : address.countryCode) ?? /* istanbul ignore next */null,
            city: (address == null ? void 0 : address.city) ?? /* istanbul ignore next */null,
            postalCode: (address == null ? void 0 : address.postalCode) ?? /* istanbul ignore next */null,
            subdivision: (address == null ? void 0 : address.state) ?? null,
            streetAddress: null,
            addressLine1: null,
            addressLine2: null
          }
        }
      }
    }));
    const expressModel = ExpressModel.fromV1(checkout, this.getModelContextFromV1(checkout));
    const shippingMethods = getShippingMethods(await this.fetchDeliveryOptionsForV1(checkout), expressModel.deliveryMethodCode);
    return {
      expressModel,
      shippingMethods
    };
  }

  // eslint-disable-next-line sonarjs/cognitive-complexity
  async updateDeliveryAndBillingInfoV1(checkoutId, billingContact, shippingContact, _ref5) {
    var _this$checkoutApi3, _this$checkoutApi4;
    let {
      shouldUpdateShippingAddress,
      wasShippingMethodSelected
    } = _ref5;
    await ((_this$checkoutApi3 = this.checkoutApi) == null ? void 0 : _this$checkoutApi3.updateCheckout(checkoutId, {
      buyerInfo: {
        email: billingContact == null ? void 0 : billingContact.email
      },
      shippingInfo: {
        shippingDestination: {
          ...(shouldUpdateShippingAddress && {
            address: {
              country: (shippingContact == null ? void 0 : shippingContact.countryCode) ?? null,
              subdivision: (shippingContact == null ? void 0 : shippingContact.state) ?? null,
              postalCode: (shippingContact == null ? void 0 : shippingContact.postalCode) ?? null,
              city: (shippingContact == null ? void 0 : shippingContact.city) ?? null,
              streetAddress: null,
              addressLine1: (shippingContact == null ? void 0 : shippingContact.addressLine1) ?? null,
              addressLine2: (shippingContact == null ? void 0 : shippingContact.addressLine2) ?? null
            }
          }),
          contactDetails: {
            firstName: (shippingContact == null ? void 0 : shippingContact.firstName) ?? null,
            lastName: (shippingContact == null ? void 0 : shippingContact.lastName) ?? null,
            phone: (shippingContact == null ? void 0 : shippingContact.phone) ?? null,
            company: null,
            vatId: null
          }
        }
      }
    }));
    const checkout = await ((_this$checkoutApi4 = this.checkoutApi) == null ? void 0 : _this$checkoutApi4.updateCheckout(checkoutId, {
      billingInfo: {
        address: {
          country: (billingContact == null ? void 0 : billingContact.countryCode) ?? null,
          postalCode: (billingContact == null ? void 0 : billingContact.postalCode) ?? null,
          city: (billingContact == null ? void 0 : billingContact.city) ?? null,
          subdivision: (billingContact == null ? void 0 : billingContact.state) ?? null,
          streetAddress: null,
          addressLine1: null,
          addressLine2: null
        },
        contactDetails: {
          firstName: (billingContact == null ? void 0 : billingContact.firstName) ?? null,
          lastName: (billingContact == null ? void 0 : billingContact.lastName) ?? null,
          phone: (billingContact == null ? void 0 : billingContact.phone) ?? null,
          company: null,
          vatId: null
        }
      }
    }));
    const expressModel = ExpressModel.fromV1(checkout, this.getModelContextFromV1(checkout));
    let shouldRedirectToCollectShipping = false;
    let hasShippingZipCodeError = false;
    let hasShippingSubdivisionError = false;
    if (shouldUpdateShippingAddress && hasShippableItems(expressModel) && !wasShippingMethodSelected) {
      var _getShippingMethods, _deliveryOptions$erro, _deliveryOptions$erro2;
      const deliveryOptions = await this.fetchDeliveryOptionsForV1(checkout);
      shouldRedirectToCollectShipping = ((_getShippingMethods = getShippingMethods(deliveryOptions, expressModel.deliveryMethodCode)) == null ? void 0 : _getShippingMethods.length) !== 1;
      hasShippingZipCodeError = ((_deliveryOptions$erro = deliveryOptions.errors) == null ? void 0 : _deliveryOptions$erro.hasShippingZipCodeError) ?? /* istanbul ignore next */false;
      hasShippingSubdivisionError = ((_deliveryOptions$erro2 = deliveryOptions.errors) == null ? void 0 : _deliveryOptions$erro2.hasShippingSubdivisionError) ?? /* istanbul ignore next */false;
    }
    return {
      expressModel,
      shouldRedirectToCollectShipping,
      hasShippingZipCodeError,
      hasShippingSubdivisionError
    };
  }
  async updateDeliveryAndBillingInfo(checkoutId, billingContact, shippingContact, _ref6) {
    let {
      shouldUpdateShippingAddress
    } = _ref6;
    const publicApi = await this.controllerConfig.wixCodeApi.site.getPublicAPI(ECOM_PLATFORM_APP_DEF_ID);
    const cart = await publicApi.__internal.updateCart(checkoutId, {
      customerInfo: {
        email: billingContact == null ? void 0 : billingContact.email,
        ...cashierContactToUpdateContactRequest(shippingContact)
      },
      ...(shouldUpdateShippingAddress && {
        deliveryInfo: {
          address: cashierContactToUpdateAddressRequest(shippingContact)
        }
      }),
      paymentInfo: {
        billingAddress: cashierContactToUpdateAddressRequest(billingContact),
        billingContact: cashierContactToUpdateContactRequest(billingContact)
      }
    });
    return {
      lineItems: (cart.lineItems ?? /* istanbul ignore next */[]).map(lineItem => LineItemModel.fromV2(lineItem, this.getModelContext(cart))),
      purchaseFlowId: cart.purchaseFlowId
    };
  }
  async setDeliveryMethod(checkoutId, code) {
    var _this$checkoutApi5;
    if (this.useCartDirectlyFromV2(checkoutId)) {
      const publicApi = await this.controllerConfig.wixCodeApi.site.getPublicAPI(ECOM_PLATFORM_APP_DEF_ID);
      const cart = await publicApi.__internal.setDeliveryMethod(checkoutId, {
        code
      });
      const {
        summary
      } = await publicApi.__internal.calculateCart(checkoutId);
      return ExpressModel.fromV2(cart, summary, this.getModelContext(cart));
    }
    const checkout = await ((_this$checkoutApi5 = this.checkoutApi) == null ? void 0 : _this$checkoutApi5.updateCheckout(checkoutId, {
      shippingInfo: {
        selectedCarrierServiceOption: {
          code,
          carrierId: null
        }
      }
    }));
    return ExpressModel.fromV1(checkout, this.getModelContextFromV1(checkout));
  }
  getModelContext(cart) {
    var _cart$customerInfo, _cart$businessInfo, _cart$paymentInfo, _cart$customerInfo2;
    return {
      useConvertedPrices: ((_cart$customerInfo = cart.customerInfo) == null ? void 0 : _cart$customerInfo.currencyCode) !== ((_cart$businessInfo = cart.businessInfo) == null ? void 0 : _cart$businessInfo.currencyCode) && ((_cart$paymentInfo = cart.paymentInfo) == null ? void 0 : _cart$paymentInfo.currencyCode) === ((_cart$customerInfo2 = cart.customerInfo) == null ? void 0 : _cart$customerInfo2.currencyCode),
      experiments: this.controllerConfig.essentials.experiments
    };
  }
  getModelContextFromV1(response) {
    return {
      useConvertedPrices: response.conversionCurrency !== response.currency && response.paymentCurrency === response.conversionCurrency,
      experiments: this.controllerConfig.essentials.experiments
    };
  }
}
//# sourceMappingURL=CheckoutService.js.map